// src/components/ModalUserDetail.jsx

import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback
} from 'react';
import API from '../api/client';
import { AuthContext } from '../context/AuthContext';
import { ModalContext } from '../context/ModalContext';
import { TabContext } from '../context/TabContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';
import ModalLocation from './ModalLocation';
import './ModalUserDetail.css';

export default function ModalUserDetail() {
  const { user, updateUser } = useContext(AuthContext);
  const { modalStacks, updateModal, openModal, closeModal } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);
  const { registerHeaderActions, unregisterHeaderActions } = useContext(ModalHeaderContext);
  const bodyRef = useRef(null);

  // Detectar si el usuario vino por Google
  const isGoogleUser = user.auth_provider === 'google';


  // 1️⃣ Extraer este modal de la pila
  const [userDetailModal, setUserDetailModal] = useState(null);
  useEffect(() => {
    const stack = modalStacks[activeTab] || [];
    const found = stack.slice().reverse()
      .find(m => m.metadata?.modalType === 'userDetail');
    setUserDetailModal(found || null);
  }, [modalStacks, activeTab]);

  // 2️⃣ Modo del modal: 'view' o 'edit'
  const modalMode = userDetailModal?.metadata?.modalMode || 'view';

  // 3️⃣ Estado del formulario, incluyendo el campo de contraseña actual
  const [formValues, setFormValues] = useState({
    username:        user.username,
    email:           user.email,
    firstName:       user.first_name || '',
    lastName:        user.last_name  || '',
    address:         user.address    || '',
    latitude:  user.latitude   || null,
    longitude: user.longitude  || null,
    phone:           (user.phone || '').replace(/^\+34/, ''),
    currentPassword: '',         // 3️⃣a) Para validar contraseña actual
    password:        '',         // 3️⃣b) Nueva contraseña
    password2:       ''          // 3️⃣c) Confirmar nueva contraseña
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  // 4️⃣ Validaciones “en línea” para usuario/email
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage,   setUsernameMessage]   = useState('');
  const [emailAvailable,    setEmailAvailable]    = useState(null);
  const [emailMessage,      setEmailMessage]      = useState('');

  // 5️⃣ Validación de requisitos de la nueva contraseña
  const [lengthValid, setLengthValid] = useState(false);
  const [upperValid,  setUpperValid]  = useState(false);
  const [lowerValid,  setLowerValid]  = useState(false);
  const [numberValid, setNumberValid] = useState(false);

  // 5️⃣b) Control de “¿queremos cambiar contraseña?”
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const debounceUsername = useRef(null);
  const debounceEmail    = useRef(null);
  const formValuesRef    = useRef(formValues);
  useEffect(() => { formValuesRef.current = formValues; }, [formValues]);

  // 6️⃣ Al escribir la nueva contraseña, actualizamos requisitos
  useEffect(() => {
    const pwd = formValues.password;
    setLengthValid(pwd.length >= 6);
    setUpperValid(/[A-Z]/.test(pwd));
    setLowerValid(/[a-z]/.test(pwd));
    setNumberValid(/[0-9]/.test(pwd));
  }, [formValues.password]);

  // 7️⃣ Comprobar disponibilidad de usuario
  useEffect(() => {
    const name = formValues.username.trim();
    setUsernameAvailable(null);
    setUsernameMessage('');
    clearTimeout(debounceUsername.current);
    if (!name) return;
    if (name === user.username) {
      setUsernameAvailable(true);
      setUsernameMessage('Es tu nombre de usuario actual');
      return;
    }
    debounceUsername.current = setTimeout(async () => {
      const regex = /^[a-zA-Z0-9_]{6,}$/;
      if (!regex.test(name)) {
        setUsernameAvailable(false);
        setUsernameMessage('Mínimo 6 caracteres alfanuméricos/guión bajo');
        return;
      }
      try {
        const resp = await API.get(`/auth/check_username/?username=${encodeURIComponent(name)}`);
        setUsernameAvailable(resp.data.available);
        setUsernameMessage(
          resp.data.available ? 'Usuario disponible' : 'Usuario no disponible'
        );
      } catch {
        setUsernameAvailable(false);
        setUsernameMessage('Error comprobando nombre');
      }
    }, 500);
    return () => clearTimeout(debounceUsername.current);
  }, [formValues.username, user.username]);

  // 8️⃣ Comprobar disponibilidad de email
  useEffect(() => {
    const mail = formValues.email.trim();
    setEmailAvailable(null);
    setEmailMessage('');
    clearTimeout(debounceEmail.current);
    if (!mail) return;
    if (mail === user.email) {
      setEmailAvailable(true);
      setEmailMessage('Es tu email actual');
      return;
    }
    debounceEmail.current = setTimeout(async () => {
      try {
        const resp = await API.get(`/auth/check_email/?email=${encodeURIComponent(mail)}`);
        setEmailAvailable(resp.data.available);
        setEmailMessage(
          resp.data.available ? 'Email disponible' : 'Email ya registrado'
        );
      } catch {
        setEmailAvailable(false);
        setEmailMessage('Error comprobando email');
      }
    }, 500);
    return () => clearTimeout(debounceEmail.current);
  }, [formValues.email, user.email]);

  // 9️⃣ Control del campo teléfono (solo dígitos, max 9)
  const handlePhoneChange = e => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
    setFormValues(f => ({ ...f, phone: digits }));
  };

  // 🔟 Guardar cambios (invocado desde el header)
  const handleSave = useCallback(async () => {
    const {
      username, email, firstName, lastName,
      address, phone, currentPassword, password, password2,
      latitude, longitude
    } = formValuesRef.current;

    console.log(`[ModalUserDetail] handleSave coords → lat: ${latitude}, lng: ${longitude}`);

    if (bodyRef.current) {
        bodyRef.current.scrollTop = 0;
    }

    // 1) Validaciones básicas
    if (!username || !email) {
      setError('Usuario y email son obligatorios.');
      return;
    }

    // 2) Si estamos cambiando contraseña, validamos los tres campos
    if (isChangingPassword) {
      if (!currentPassword || !password || !password2) {
        setError('Debes rellenar los tres campos de contraseña.');
        return;
      }
      if (password !== password2) {
        setError('La nueva contraseña y su confirmación no coinciden.');
        return;
      }
      if (!lengthValid || !upperValid || !lowerValid || !numberValid) {
        setError('La contraseña no cumple los requisitos de seguridad.');
        return;
      }
    }

    // 3) Validación online de usuario/email
    if (username !== user.username && usernameAvailable === false) {
      setError('Corrige el nombre de usuario antes de guardar.');
      return;
    }
    if (emailAvailable === false) {
      setError('Corrige el email antes de guardar.');
      return;
    }

    // 4) Construir payload parcial
    const payload = {};
    if (username  !== user.username)     payload.username   = username;
    if (email     !== user.email)        payload.email      = email;
    if (firstName !== user.first_name)   payload.first_name = firstName;
    if (lastName  !== user.last_name)    payload.last_name  = lastName;
    if (address   !== (user.address||''))payload.address    = address;
    // → GRABAMOS siempre lat_write/lng_write (pueden ser null para borrar)
    payload.lat_write = latitude;
    payload.lng_write = longitude;
    if (phone && `+34${phone}` !== (user.phone||'')) payload.phone = `+34${phone}`;

    // 5️⃣ Añadir contraseña al payload solo si cambiamos
    if (isChangingPassword) {
      payload.current_password = currentPassword; // si el backend lo soporta
      payload.password         = password;
    }

    // 6️⃣ Si no hay cambios, volvemos a view
    if (Object.keys(payload).length === 0) {
      return updateModal(null, {
        tab: activeTab,
        metadata: { modalType: 'userDetail', modalMode: 'view' }
      });
    }

    setError(null);
    try {
      // 7️⃣ Llamada PATCH parcial
      const resp = await API.patch('/auth/user/', payload);

      // 8️⃣ Merge para no perder auth_provider, etc.
      updateUser({ ...user, ...resp.data });

      // 9️⃣ Volver a view y reset de cambio de contraseña
      updateModal(null, {
        tab: activeTab,
        metadata: { modalType: 'userDetail', modalMode: 'view' }
      });
      setIsChangingPassword(false);
      setFormValues(f => ({
        ...f,
        currentPassword: '',
        password: '',
        password2: ''
      }));
      setSuccess('Datos actualizados correctamente.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error guardando cambios.');
    }
  }, [
    activeTab, user, updateUser, updateModal,
    usernameAvailable, emailAvailable,
    lengthValid, upperValid, lowerValid, numberValid,
    isChangingPassword
  ]);

  // ⓫ Registrar acciones de header (Editar, Cancelar, Guardar)
  useEffect(() => {
    if (!userDetailModal) return;
    const id = userDetailModal.id;

    // Detectar si está abierto un ModalLocation anidado
    const nested = modalStacks[activeTab]?.some(
      m => m.metadata?.modalType === 'location'
    );

    const onEdit   = () => updateModal(null, { tab: activeTab, metadata: { modalType: 'userDetail', modalMode: 'edit' } });
    const onCancel = () => updateModal(null, { tab: activeTab, metadata: { modalType: 'userDetail', modalMode: 'view' } });
    // Sólo registramos onSave si no hay modalLocation abierto
    const actions = { onEdit, onCancel };
    if (!nested) {
      actions.onSave = handleSave;
    }
    registerHeaderActions(id, actions);
    return () => unregisterHeaderActions(id);
  }, [userDetailModal, activeTab, modalStacks, updateModal, handleSave, registerHeaderActions, unregisterHeaderActions]);

  // ⓬ Al entrar en “edit”, reiniciar formularios
  useEffect(() => {
    if (modalMode === 'edit') {
      setFormValues({
        username:        user.username,
        email:           user.email,
        firstName:       user.first_name  || '',
        lastName:        user.last_name   || '',
        address:         user.address     || '',
        latitude:        user.latitude   || null,
        longitude:       user.longitude  || null,
        phone:           (user.phone||'').replace(/^\+34/, ''),
        currentPassword: '',
        password:        '',
        password2:       ''
      });
      setError(null);
      setIsChangingPassword(false);
    }
  }, [modalMode, user]);

  // ─── Función para abrir el modal de ubicación ────────────────────────
  const handleOpenLocationModal = () => {
    openModal(
      <ModalLocation
        initialLocation={{
          address: formValues.address,
          lat: formValues.latitude,
          lng: formValues.longitude
        }}
        onAccept={newLoc => {
          // Actualizamos solo el campo de dirección en el formulario
          setFormValues(f => ({
            ...f,
            address: newLoc.address,
            latitude:  newLoc.lat,
            longitude: newLoc.lng
          }));
          closeModal(activeTab);
        }}
        onCancel={() => closeModal(activeTab)}
        hideRange={true}
      />,
      { tab: activeTab,
        disableSwipe: true,
        overlayClassName: 'pet-detail-overlay'
      }
    );
  };

  // ⓭ Cerrar modal al clickar fuera
  const handleClose = () => closeModal(activeTab);

   // 🔄 Limpiar mensaje de éxito después de 10 s
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 3_000);
    return () => clearTimeout(timer);
  }, [success]);

  return (
    <div className="modalCard" ref={bodyRef} onClick={handleClose}>
      <div className="modal-body" onClick={e => e.stopPropagation()}>
        {error && <div className="error">{error}</div>}

        {(modalMode === 'view' || modalMode === 'edit') && (
          <>
            {/* Usuario */}
            <div className="form-group">
              <label>
                Usuario <span className="required">*</span>
              </label>
              <input
                value={ modalMode === 'view' ? user.username : formValues.username }
                onChange={modalMode === 'edit'
                  ? e => setFormValues(f => ({ ...f, username: e.target.value }))
                  : undefined}
                readOnly={modalMode === 'view'}
                required
              />
              {modalMode === 'edit' && usernameMessage && (
                <small className={usernameAvailable ? 'valid' : 'invalid'}>
                  {usernameMessage}
                </small>
              )}
            </div>

            {/* Email */}
            <div className="form-group">
              <label>
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                value={
                  modalMode === 'view'
                    ? user.email
                    : formValues.email
                }
                onChange={modalMode === 'edit' && !isGoogleUser
                  ? e => setFormValues(f => ({ ...f, email: e.target.value }))
                  : undefined}
                readOnly={modalMode === 'view' || isGoogleUser}
                required
              />
              {/* Solo mostramos el mensaje de disponibilidad en edit y si NO es Google */}
              {modalMode === 'edit' && !isGoogleUser && emailMessage && (
                <small className={emailAvailable ? 'valid' : 'invalid'}>
                  {emailMessage}
                </small>
              )}
            </div>

            {/* Nombre */}
            <div className="form-group">
              <label>Nombre</label>
              <input
                value={
                  modalMode === 'view'
                    ? (user.first_name || '')
                    : formValues.firstName
                }
                onChange={modalMode === 'edit'
                  ? e => setFormValues(f => ({ ...f, firstName: e.target.value }))
                  : undefined}
                readOnly={modalMode === 'view'}
              />
            </div>

            {/* Apellidos */}
            <div className="form-group">
              <label>Apellidos</label>
              <input
                value={
                  modalMode === 'view'
                    ? (user.last_name || '')
                    : formValues.lastName
                }
                onChange={modalMode === 'edit'
                  ? e => setFormValues(f => ({ ...f, lastName: e.target.value }))
                  : undefined}
                readOnly={modalMode === 'view'}
              />
            </div>

            {/* Dirección */}
            <div className="form-group">
              <label>Ciudad / Población</label>
              <input
                type="text"
                value={ modalMode === 'view'
                  ? formValues.address
                  : formValues.address
                }
                readOnly={true}  // siempre sólo lectura
              />
            </div>

            {/* ─── Botón para actualizar dirección en modo edit ──────────────── */}
            {modalMode === 'edit' && (
              <div className="password-actions">
                <button
                  type="button"
                  className="link-change-password"
                  onClick={handleOpenLocationModal}
                >
                  Actualizar dirección
                </button>
              </div>
            )}

            {/* Teléfono */}
            <div className="form-group phone-group">
              <label>Teléfono</label>
              <div className="phone-wrapper">
                <span className="phone-prefix">+34</span>
                <input
                  type="tel"
                  value={ modalMode === 'view'
                    ? (user.phone || '').replace(/^\+34/, '')
                    : formValues.phone }
                  onChange={modalMode === 'edit' ? handlePhoneChange : undefined}
                  readOnly={modalMode === 'view'}
                  className="phone-input"
                  placeholder="6XX XXX XXX"
                />
              </div>
            </div>
          </>
        )}

        {/* ─── Contraseña ───────────────────────────────────────── */}
        {!isGoogleUser && modalMode === 'edit' && (
          <>
            {/* 1) Link para iniciar el cambio */}
            {!isChangingPassword && (
              <div className="password-actions">
                <button
                  type="button"
                  className="link-change-password"
                  onClick={() => setIsChangingPassword(true)}
                >
                  Cambiar contraseña
                </button>
              </div>
            )}

            {/* 2) Campos de contraseña SOLO si isChangingPassword */}
            {isChangingPassword && (
              <>
                {/* 2.1) Contraseña actual con show/hide */}
                <div className="form-group relative">
                  <label>Contraseña actual</label>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={formValues.currentPassword}
                    onChange={e =>
                      setFormValues(f => ({ ...f, currentPassword: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowCurrentPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? '🙈' : '👁️'}
                  </button>
                </div>

                {/* 2.2) Contraseña nueva + requisitos */}
                <div className="form-group relative">
                  <label>Contraseña nueva</label>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={formValues.password}
                    onChange={e => setFormValues(f => ({ ...f, password: e.target.value }))}
                  />
                  {/* Botón ojo para mostrar/ocultar */}
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowNewPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                  {formValues.password.length > 0 && (
                    <ul className="password-requirements">
                      <li className={lengthValid ? 'valid' : 'invalid'}>Mínimo 6 caracteres</li>
                      <li className={upperValid ? 'valid' : 'invalid'}>Una mayúscula</li>
                      <li className={lowerValid ? 'valid' : 'invalid'}>Una minúscula</li>
                      <li className={numberValid ? 'valid' : 'invalid'}>Un número</li>
                    </ul>
                  )}
                </div>

                {/* 2.3) Confirmar nueva contraseña */}
                <div className="form-group relative">
                  <label>Confirmar nueva contraseña</label>
                  <input
                    type={showConfirmNewPassword ? 'text' : 'password'}
                    value={formValues.password2}
                    onChange={e => setFormValues(f => ({ ...f, password2: e.target.value }))}
                  />
                  {/* Botón ojo para mostrar/ocultar */}
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowConfirmNewPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showConfirmNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
        {success && <div className="success">{success}</div>}
      </div>
    </div>
  );
}
